/**
 * Role configuration hooks for the unified capacity model.
 *
 * Global defaults are editable on the Settings page.
 * Per-project overrides are editable on the Portfolio page.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RoleConfigRow {
  role_key: string;
  allocation_pct: number;
  planning_pct: number;
  execution_pct: number;
  testing_gl_pct: number;
  is_override?: boolean;
}

export interface RoleConfigDefaultsResponse {
  roles: RoleConfigRow[];
}

export interface ProjectRoleConfigResponse {
  project_id: string;
  est_hours: number;
  roles: RoleConfigRow[];
}

// ---------------------------------------------------------------------------
// Global defaults
// ---------------------------------------------------------------------------

export function useRoleDefaults() {
  return useQuery<RoleConfigDefaultsResponse>({
    queryKey: ["role-config", "defaults"],
    queryFn: () => api.get("/role-config/defaults").then((r) => r.data),
  });
}

export function useUpdateRoleDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roles: RoleConfigRow[]) =>
      api.put("/role-config/defaults", { roles }).then((r) => r.data),
    onSuccess: () => {
      // Changing defaults affects all project capacity calculations
      qc.invalidateQueries({ queryKey: ["role-config"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Per-project config
// ---------------------------------------------------------------------------

export function useProjectRoleConfig(projectId: string | null) {
  return useQuery<ProjectRoleConfigResponse>({
    queryKey: ["role-config", "project", projectId],
    queryFn: () =>
      api.get(`/role-config/project/${projectId}`).then((r) => r.data),
    enabled: projectId !== null,
  });
}

export function useUpdateProjectRoleConfig(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roles: RoleConfigRow[]) =>
      api.put(`/role-config/project/${projectId}`, { roles }).then((r) => r.data),
    onSuccess: () => {
      // Role config drives demand calculations everywhere — every cache
      // that reads role allocations or per-person demand has to refresh.
      // Without invalidating "roster", the Team View's per-person demand
      // cache stayed stale after a save, so removing a role on a project
      // appeared to do nothing on the Synnergie page.
      qc.invalidateQueries({ queryKey: ["role-config"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["direct"] });
    },
  });
}
